import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowUpDown, CalendarDays, Filter, Search } from "lucide-react";
import Layout from "@/components/Layout";
import StickyFilters from "@/components/StickyFilters";
import ViewToggle, { useViewMode } from "@/components/ViewToggle";
import DateRangeFilter, { EMPTY_RANGE, rangeParams } from "@/components/DateRangeFilter";
import { FilterSelect, IssuePills, Pill, ReleasedPill, SocietyDots } from "@/components/Music";
import { TrackCard, TrackDrawer, artistLine } from "@/components/Tracks";
import { EmptyState, LoadingPanel } from "@/components/Bits";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { api, apiError } from "@/lib/api";
import { useRemembered } from "@/lib/useRemembered";
import { cn } from "@/lib/utils";

const DATE_FIELDS = [
  { value: "release_date", label: "Release date" },
  { value: "original_release_date", label: "Original release" },
];

function monthLabel(key) {
  if (key === "none") return "No release date";
  const [y, m] = key.split("-");
  return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString(undefined, { month: "long", year: "numeric" });
}

export default function Releases() {
  const { user } = useAuth();
  const { societyLabel } = useSettings();
  const [mode, setMode] = useViewMode("releases", "list");
  const [range, setRange] = useState(EMPTY_RANGE);
  const [q, setQ] = useState("");
  const [distributor, setDistributor] = useState("");
  const [order, setOrder] = useState("release_desc");
  const [rows, setRows] = useRemembered("releases");
  const [values, setValues] = useState({ distributor: [] });
  const [open, setOpen] = useState(null);
  const req = useRef(0);

  const load = useCallback(async () => {
    const id = ++req.current;
    try {
      const { data } = await api.get("/tracks", { params: { q, distributor, sort: order, ...rangeParams(range) } });
      if (id === req.current) setRows(data.tracks, !q && !distributor && order === "release_desc" && !range.from && !range.to);
    } catch (err) {
      toast.error(apiError(err));
    }
  }, [q, distributor, order, range, setRows]);

  useEffect(() => {
    const t = setTimeout(load, q ? 200 : 0);
    return () => clearTimeout(t);
  }, [load, q]);
  useEffect(() => {
    api.get("/values").then(({ data }) => setValues(data)).catch(() => {});
  }, []);

  const field = range.field || "release_date";
  const today = new Date().toISOString().slice(0, 10);
  const groups = useMemo(() => {
    const g = new Map();
    for (const t of rows || []) {
      const d = t[field];
      const key = d ? d.slice(0, 7) : "none";
      if (!g.has(key)) g.set(key, []);
      g.get(key).push(t);
    }
    const keys = [...g.keys()].filter((k) => k !== "none");
    if (g.has("none")) keys.push("none");
    return keys.map((k) => [k, g.get(k)]);
  }, [rows, field]);

  const current = open ? (rows || []).find((t) => t.track_id === open) : null;
  const upcoming = (rows || []).filter((t) => t.release_date > today).length;

  return (
    <Layout title="Releases" subtitle={rows ? `${rows.length} tracks · ${upcoming} coming up` : "Loading…"}>
      <StickyFilters className="-mt-3 mb-2">
        <div className="flex items-center gap-2 flex-wrap">
          <label className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted2" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" aria-label="Search" className="h-9 w-[220px] pl-9 pr-3 rounded-lg border border-line bg-card text-[13px] outline-none focus:border-brand-ring" />
          </label>
          <DateRangeFilter value={range} onChange={setRange} fields={DATE_FIELDS} />
          <FilterSelect value={distributor} onChange={setDistributor} allLabel="All distributors" icon={Filter} options={values.distributor.filter((v) => v.count)} />
          <div className="flex-1" />
          <FilterSelect
            value={order === "release_desc" ? "" : order}
            onChange={(v) => setOrder(v || "release_desc")}
            allLabel="Newest first"
            icon={ArrowUpDown}
            ariaLabel="Order"
            options={[{ value: "release_asc", label: "Oldest first" }]}
          />
          <ViewToggle mode={mode} onChange={setMode} />
        </div>
      </StickyFilters>

      {!rows ? (
        <LoadingPanel rows={6} />
      ) : rows.length === 0 ? (
        <div className="panel">
          <EmptyState icon={CalendarDays} title="No releases in this period" body="Try another date range." />
        </div>
      ) : (
        <div className="space-y-6">
          {groups.map(([key, list]) => (
            <section key={key}>
              <div className="flex items-baseline gap-3 mb-2.5">
                <h2 className={cn("font-display text-[18px] font-semibold", key === "none" && "text-muted2")}>{monthLabel(key)}</h2>
                <span className="font-mono text-[11.5px] text-muted2">{list.length}</span>
                {key !== "none" && key > today.slice(0, 7) && <Pill tone="brand">Upcoming</Pill>}
              </div>
              {mode === "grid" ? (
                <div className="grid sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-3">
                  {list.map((t) => (
                    <TrackCard key={t.track_id} t={t} onOpen={(x) => setOpen(x.track_id)} labels={societyLabel} />
                  ))}
                </div>
              ) : (
                <div className="panel overflow-hidden">
                  {list.map((t) => {
                    const d = t[field] ? new Date(`${t[field]}T00:00:00`) : null;
                    return (
                      <button key={t.track_id} onClick={() => setOpen(t.track_id)} className="w-full text-left flex items-center gap-4 px-4 py-3 border-b border-line/70 last:border-0 hover:bg-brand-light/40">
                        <div className="w-11 text-center shrink-0">
                          {d ? (
                            <>
                              <div className="font-mono text-[10px] tracking-[0.12em] text-muted2">{d.toLocaleDateString(undefined, { weekday: "short" }).toUpperCase()}</div>
                              <div className="font-display text-[20px] font-semibold leading-none">{d.getDate()}</div>
                            </>
                          ) : (
                            <div className="text-muted2">—</div>
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-[13.5px] font-semibold truncate">{t.title}</div>
                          <div className="text-[12px] text-muted2 truncate">{artistLine(t)}</div>
                        </div>
                        <div className="hidden md:block font-mono text-[12px] text-muted2 w-[120px] truncate">{t.isrc}</div>
                        <div className="hidden lg:block w-[130px]">{t.distributor && <Pill tone="dist">{t.distributor}</Pill>}</div>
                        <div className="hidden sm:block">
                          <ReleasedPill value={t.released} />
                        </div>
                        <div className="hidden xl:block">
                          <SocietyDots regs={t.regs} size="sm" labels={societyLabel} />
                        </div>
                        <div className="hidden 2xl:block w-[200px]">
                          <IssuePills issues={t.issues} max={1} />
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </section>
          ))}
        </div>
      )}
      <TrackDrawer track={current} open={!!current} onOpenChange={(v) => !v && setOpen(null)} canEdit={user?.role !== "writer"} />
    </Layout>
  );
}
